import { Box, Chip, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { startCase } from 'lodash';
import { SectionCard } from '@modules/talents/components/SectionCard';
import { SectionTitle } from '@modules/talents/components/SectionTitle';

interface JobOfferCardProps {
    jobOffer: {
        _id: string;
        title: string;
        city?: string;
        jobNumber?: string;
        active?: boolean;
        contract?: {
            contractType?: string;
            remuneration?: { min?: number; max?: number };
            isAnnualSalary?: boolean;
            showSalary?: boolean;
        } | null;
    };
}

const JobOfferCard = ({ jobOffer }: JobOfferCardProps) => {
    const theme = useTheme();
    const { contract } = jobOffer;

    const salaryLabel = (() => {
        if (!contract?.showSalary) return null;
        const min = contract?.remuneration?.min;
        const max = contract?.remuneration?.max;
        if (!min && !max) return null;
        const unit = contract?.isAnnualSalary ? '€/an' : '€/h';
        if (min && max) return `${min.toLocaleString()} – ${max.toLocaleString()} ${unit}`;
        if (min) return `dès ${min.toLocaleString()} ${unit}`;
        return `jusqu'à ${max!.toLocaleString()} ${unit}`;
    })();

    return (
        <SectionCard>
            <SectionTitle title="Offre d'emploi" />
            <Box sx={{ mt: 1 }}>
                    <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        component={jobOffer.jobNumber ? Link : 'span'}
                        to={jobOffer.jobNumber ? `/job-offers/${jobOffer.jobNumber}/detail` : undefined}
                        sx={{
                            color: jobOffer.jobNumber ? theme.palette.neutralWhite.main : 'inherit',
                            textDecoration: jobOffer.jobNumber ? 'underline' : 'none',
                            textDecorationColor: jobOffer.jobNumber ? theme.palette.neutralWhite.main : undefined,
                            display: 'block',
                            mb: 0.5,
                        }}
                    >
                        {jobOffer.title}
                    </Typography>
                    {jobOffer.jobNumber && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}>
                            #{jobOffer.jobNumber}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        {jobOffer.city && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {startCase(jobOffer.city.toLowerCase())}
                            </Typography>
                        )}
                        {contract?.contractType && (
                            <Chip label={contract.contractType} size="small" variant="outlined" sx={{ height: 18, fontSize: '10px' }} />
                        )}
                        {salaryLabel && (
                            <Chip label={salaryLabel} size="small" sx={{ height: 18, fontSize: '10px', bgcolor: theme.palette.transparentPurple.main }} />
                        )}
                        <Chip
                            label={jobOffer.active ? 'Active' : 'Inactive'}
                            size="small"
                            color={jobOffer.active ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '10px' }}
                        />
                    </Box>
                </Box>
        </SectionCard>
    );
};

export default JobOfferCard;
